import moment from 'moment'
import { emoteUrls } from '../extras/playerEmotes'
import { readPacket, writePacket } from '../packets'
import { storage } from '../storage'
import { uuid } from '../utils'
import { hashFile } from '../utils-client'
import { System } from './System'
import axios from 'axios'

/**
 * Client Network System
 *
 * - runs on the client
 * - provides abstract network methods matching ServerNetwork
 *
 */
export class ClientNetwork extends System {
  constructor(world) {
    super(world)
    this.ids = -1
    this.ws = null
    this.apiUrl = null
    this.id = null
    this.isClient = true
    this.queue = []
  }

  init({ wsUrl, name, avatar }) {
    const authToken = storage.get('authToken')
    let url = `${wsUrl}?authToken=${authToken}&world=${this.world.name}`
    if (name) url += `&name=${encodeURIComponent(name)}`
    if (avatar) url += `&avatar=${encodeURIComponent(avatar)}`
    this.ws = new WebSocket(url)
    this.ws.binaryType = 'arraybuffer'
    this.ws.addEventListener('message', this.onPacket)
    this.ws.addEventListener('close', this.onClose)
  }

  preFixedUpdate() {
    this.flush()
  }

  send(name, data) {
    // console.log('->', name, data)
    const packet = writePacket(name, data)
    this.ws.send(packet)
  }

  async upload(file) {
    {
      // first check if we even need to upload it
      const hash = await hashFile(file)
      const ext = file.name.split('.').pop().toLowerCase()
      const filename = `${hash}.${ext}`
      const url = `${this.apiUrl}/upload-check?filename=${filename}`
      const resp = await fetch(url)
      const data = await resp.json()
      console.log('exist data', data)
      if (data.exists) return // console.log('already uploaded:', filename)
    }
    // then upload it
    const form = new FormData()
    form.append('file', file)
    const url = `${this.apiUrl}/upload`
    await fetch(url, {
      method: 'POST',
      body: form,
    })
  }

  async uploadInscription(id) {
    try {
      console.log('Upload process started...')

      const apiData = { id }
      const apiUrl = `${this.apiUrl}/upload-inscription`
      const res = await axios.post(apiUrl, apiData)
      console.log('API Response:', res.data)

      const { fileName } = res.data
      const url = `asset://${fileName}`

      const transform = this.world.builder.getSpawnTransform()

      const boundary = this.world.boundary
      // console.log('boundary', boundary)
      console.log('transform position', transform.position)
      if (boundary && typeof boundary.isInsideXY === 'function') {
        let position

        if (transform.position instanceof THREE.Vector3) {
          position = transform.position
          console.log("position 1", position)
        } else if (Array.isArray(transform.position) && transform.position.length === 3) {
          position = new THREE.Vector3(transform.position[0], transform.position[1], transform.position[2])
          console.log("position 2", position)
        } else if (
          transform.position.x !== undefined &&
          transform.position.y !== undefined &&
          transform.position.z !== undefined
        ) {
          position = new THREE.Vector3(transform.position.x, transform.position.y, transform.position.z)
          console.log("position 3", position)
        } else {
          console.error('Invalid position format', transform.position)
        return
      }

      if (!boundary.isInsideXY(position)) {
          alert('You cannot upload a file outside the allowed area.')
        return
      }
      }

      const blueprint = {
        id: uuid(),
        version: 0,
        name: fileName,
        image: null,
        author: null,
        url: null,
        desc: null,
        model: url,
        script: null,
        props: {},
        preload: false,
        public: false,
        locked: false,
        unique: false,
        scene: false,
        disabled: false,
        world: this.world.name,
      }
      this.world.blueprints.add(blueprint, true)
      console.log('Blueprint created:', blueprint)

      // Create app entity with default position
      const data = {
        id: uuid(),
        type: 'app',
        blueprint: blueprint.id,
        position: transform.position,
        quaternion: new THREE.Quaternion(), // default rotation
        scale: [1, 1, 1],
        mover: null,
        uploader: this.world.network.id,
        pinned: false,
        state: {},
        world: this.world.name,
      }
      const app = this.world.entities.add(data, true)
      console.log('Entity added:', data)

      app.onUploaded()
      console.log('Entity marked as uploaded')

      return res.data
    } catch (error) {
      console.error('Upload inscription process failed:', error)
      throw error
    }
  }

  enqueue(method, data) {
    this.queue.push([method, data])
  }

  flush() {
    while (this.queue.length) {
      try {
        const [method, data] = this.queue.shift()
        this[method]?.(data)
      } catch (err) {
        console.error(err)
      }
    }
  }

  getTime() {
    return (performance.now() + this.serverTimeOffset) / 1000 // seconds
  }

  onPacket = e => {
    const [method, data] = readPacket(e.data)
    this.enqueue(method, data)
    // console.log('<-', method, data)
  }

  onSnapshot(data) {
    this.id = data.id
    this.serverTimeOffset = data.serverTime - performance.now()
    this.apiUrl = data.apiUrl
    this.maxUploadSize = data.maxUploadSize
    this.world.assetsUrl = data.assetsUrl

    // preload environment model and avatar
    // if (this.world.environment.base) {
    //   this.world.loader.preload('model', this.world.environment.base.model)
    // }
    if (data.settings.avatar) {
      this.world.loader.preload('avatar', data.settings.avatar.url)
    }
    // preload some blueprints
    for (const item of data.blueprints) {
      if (item.preload && !item.disabled) {
        if (item.model) {
          const type = item.model.endsWith('.vrm') ? 'avatar' : 'model'
          this.world.loader.preload(type, item.model)
        }
        if (item.script) {
          this.world.loader.preload('script', item.script)
        }
        for (const value of Object.values(item.props || {})) {
          if (value === undefined || value === null || !value?.url || !value?.type) continue
          this.world.loader.preload(value.type, value.url)
        }
      }
    }
    // preload emotes
    for (const url of emoteUrls) {
      this.world.loader.preload('emote', url)
    }
    // preload local player avatar
    for (const item of data.entities) {
      if (item.type === 'player' && item.owner === this.id) {
        const url = item.sessionAvatar || item.avatar
        this.world.loader.preload('avatar', url)
      }
    }
    this.world.loader.execPreload()

    this.world.collections.deserialize(data.collections)
    this.world.settings.deserialize(data.settings)
    this.world.settings.setHasAdminCode(data.hasAdminCode)
    this.world.chat.deserialize(data.chat)
    this.world.blueprints.deserialize(data.blueprints)
    this.world.entities.deserialize(data.entities)
    this.world.livekit?.deserialize(data.livekit)
    // bitmap world: remember the plot grid + the plots assigned to this user, and show the picker
    this.world.isBitmap = !!data.isBitmap
    this.world.plotGrid = data.plotGrid || null
    this.world.plots = Array.isArray(data.plots) ? data.plots : []
    console.log(`[snapshot] isBitmap=${this.world.isBitmap}, plotGrid=`, this.world.plotGrid, `plots=${this.world.plots.length}`)
    if (this.world.plotGrid) {
      // render a wall set around every plot using the existing Boundary system
      if (this.world.boundary?.setPlotGrid) {
        this.world.boundary.setPlotGrid(this.world.plotGrid)
      } else {
        console.warn('[snapshot] world.boundary.setPlotGrid missing')
      }
      this.world.emit('plotGrid', this.world.plotGrid)
    }
    if (this.world.plots.length > 0) {
      this.world.emit('plots', this.world.plots)
    }
    storage.set('authToken', data.authToken)
  }

  onSettingsModified = data => {
    this.world.settings.set(data.key, data.value)
  }

  onChatAdded = msg => {
    this.world.chat.add(msg, false)
  }

  onChatCleared = () => {
    this.world.chat.clear()
  }

  onBlueprintAdded = blueprint => {
    this.world.blueprints.add(blueprint)
  }

  onBlueprintModified = change => {
    this.world.blueprints.modify(change)
  }

  onEntityAdded = data => {
    this.world.entities.add(data)
  }

  onEntityModified = data => {
    const entity = this.world.entities.get(data.id)
    if (!entity) return console.error('onEntityModified: no entity found', data)
    entity.modify(data)
  }

  onEntityEvent = event => {
    const [id, version, name, data] = event
    const entity = this.world.entities.get(id)
    entity?.onEvent(version, name, data)
  }

  onEntityRemoved = id => {
    this.world.entities.remove(id)
  }

  onPlayerTeleport = data => {
    this.world.entities.player?.teleport(data)
  }

  onPlayerPush = data => {
    this.world.entities.player?.push(data.force)
  }

  onPlayerSessionAvatar = data => {
    this.world.entities.player?.setSessionAvatar(data.avatar)
  }

  onLiveKitLevel = data => {
    this.world.livekit.setLevel(data.playerId, data.level)
  }

  onMute = data => {
    this.world.livekit.setMuted(data.playerId, data.muted)
  }

  onPong = time => {
    this.world.stats?.onPong(time)
  }

  onKick = code => {
    this.world.emit('kick', code)
  }

  onClose = code => {
    this.world.chat.add({
      id: uuid(),
      from: null,
      fromId: null,
      body: `You have been disconnected.`,
      createdAt: moment().toISOString(),
    })
    this.world.emit('disconnect', code || true)
    console.log('disconnect', code)
  }

  destroy() {
    if (this.ws) {
      this.ws.removeEventListener('message', this.onPacket)
      this.ws.removeEventListener('close', this.onClose)
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
      this.ws = null
    }
  }
}
